import { SerializableState } from '@domain/states/SerializableState'
import { ICachedHttpClient } from '@infra/cachedHttp/ICachedHttpClient'
import { LocalCachedHttpClient } from '@infra/cachedHttp/LocalCachedHttpClient'
import { AxiosHttpClient } from '@infra/http/AxiosHttpClient'
import { IHttpClient } from '@infra/http/IHttpClient'
import { IKeyValuePersister } from '@infra/keyValuePersister/IKeyValuePersister'
import { LocalStorageKeyValuePersister } from '@infra/keyValuePersister/LocalStorageKeyValuePersister'
import { ILogger } from '@infra/logger/ILogger'
import { LocalLogger } from '@infra/logger/LocalLogger'
import { IService } from '@services/IService'
import { StatePersister } from '@services/statePersister/StatePersister'
import { Container, interfaces } from 'inversify'

import { AuthState } from '@/domain/states/AuthState'
import { FlightState } from '@/domain/states/FlightState'
import { MapLayerState } from '@/domain/states/MapLayerState'
import { MapState } from '@/domain/states/MapState'
import { ProceduresState } from '@/domain/states/ProceduresState'
import { RouteNavigationState } from '@/domain/states/RouteNavigationState'
import { RouteState } from '@/domain/states/RouteState'
import { SettingsState } from '@/domain/states/SettingsState'
import { UserChartUploadState } from '@/domain/states/UserChartUploadState'
import { UserGeometryLayerVisibilityState } from '@/domain/states/UserGeometryLayerVisibilityState'
import { UserGeometryUploadState } from '@/domain/states/UserGeometryUploadState'
import { UserWaypointVisibilityState } from '@/domain/states/UserWaypointVisibilityState'
import { UndoRouteUseCase } from '@/domain/useCases/Route/undoRoute'
import {
  CheckUserChartNameExistsUseCase,
  DeleteUserChartsLocalDataUseCase,
  DeleteUserChartUseCase,
  DownloadUserChartOriginalUseCase,
  LoadUserChartsUseCase,
  LoadUserChartUseCase,
  PrepareUserChartUseCase,
  RefreshUserChartsUseCase,
  SaveUserChartUseCase,
  UpdateUserChartUseCase
} from '@/domain/useCases/UserCharts'
import {
  CheckUserGeometryFileExistsUseCase,
  CheckUserGeometryNameExistsUseCase,
  DeleteUserGeometriesLocalDataUseCase,
  DeleteUserGeometryUseCase,
  LoadUserGeometriesUseCase,
  LoadUserGeometryUseCase,
  PrepareUserGeometryUseCase,
  SaveUserGeometryUseCase,
  UpdateUserGeometryUseCase
} from '@/domain/useCases/UserGeometries'
import { UpdateGeometriesLayersUseCase } from '@/domain/useCases/UserGeometriesLayers/updateGeometriesLayers'

import { Authenticator } from '@/services/Authenticator/Authenticator'
import { CruiseAltitudeSuggester } from '@/services/cruiseAltitudeSuggester/CruiseAltitudeSuggester'
import { MetricsService } from '@/services/Metrics/MetricsService'
import { RouteStateSideEffects } from '@/services/stateSideEffects/RouteStateSideEffects'
import { ITrackingService } from '@/services/Tracking/ITrackingService'
import { TrackingService } from '@/services/Tracking/TrackingService'
import { UserChartSyncService } from '@/services/userChartSync/UserChartSyncService'
import { UserChartTilesPrefetchService } from '@/services/userChartTilesPrefetch/UserChartTilesPrefetchService'
import { UserGeometrySyncService } from '@/services/userGeometrySync/UserGeometrySyncService'

import { AeroInfoRepository } from '@/data/AeroInfoRepository/AeroInfoRepository'
import { IAeroInfoRepository } from '@/data/AeroInfoRepository/IAeroInfoRepository'
import { AircraftModelRepository } from '@/data/AircraftModelRepository/AircraftModelRepository'
import { IAircraftModelRepository } from '@/data/AircraftModelRepository/IAircraftModelRepository'
import { IAirspacesRepository } from '@/data/AirspacesRepository/IAirspacesRepository'
import { JetStreamAirspacesRepository } from '@/data/AirspacesRepository/JetStreamAirspacesRepository'
import { DefaultWeightsRepository } from '@/data/DefaultWeightsRepository/DefaultWeightsRepository'
import { IDefaultWeightsRepository } from '@/data/DefaultWeightsRepository/IDefaultWeightsRepository'
import { FeatureFlagsRepository } from '@/data/FeatureFlagsRepository/FeatureFlagsRepository'
import { IFeatureFlagsRepository } from '@/data/FeatureFlagsRepository/IFeatureFlagsRepository'
import { FuelRepository } from '@/data/FuelRepository/FuelRepository'
import { IFuelRepository } from '@/data/FuelRepository/IFuelRepository'
import { IMeteorologyRepository } from '@/data/MeteorologyRepository/IMeteorologyRepository'
import { RemoteMeteorologyRepository } from '@/data/MeteorologyRepository/RemoteMeteorologyRepository'
import { INavaidRepository } from '@/data/NavaidRepository/INavaidRepository'
import { NavaidRepository } from '@/data/NavaidRepository/NavaidRepository'
import { INotamRepository } from '@/data/NotamRepository/INotamRepository'
import { NotamRepository } from '@/data/NotamRepository/NotamRepository'
import { IObstacleRepository } from '@/data/ObstacleRepository/IObstacleRepository'
import { ObstacleRepository } from '@/data/ObstacleRepository/ObstacleRepository'
import { IProceduresRepository } from '@/data/ProcedureChartsRepository/IProceduresRepository'
import { ProceduresRepository } from '@/data/ProcedureChartsRepository/ProceduresRepository'
import { IUserAircraftsRepository } from '@/data/UserAircraftsRepository/IUserAircraftsRepository'
import { UserAircraftsRepository } from '@/data/UserAircraftsRepository/UserAircraftsRepository'
import { IUserChartRepository } from '@/data/UserChartRepository/IUserChartRepository'
import { UserChartRepository } from '@/data/UserChartRepository/UserChartRepository'
import { IUserGeometryRepository } from '@/data/UserGeometryRepository/IUserGeometryRepository'
import { UserGeometryRepository } from '@/data/UserGeometryRepository/UserGeometryRepository'
import { IUserRoutesRepository } from '@/data/UserRoutesRepository/IUserRoutesRepository'
import { UserRoutesRepository } from '@/data/UserRoutesRepository/UserRoutesRepository'
import { IUserWaypointsRepository } from '@/data/UserWaypointsRepository/IUserWaypointsRepository'
import { UserWaypointsRepository } from '@/data/UserWaypointsRepository/UserWaypointsRepository'
import { IWBAirplaneFillRepository } from '@/data/WBAirplaneFillRepository/IWBAirplaneFillRepository'
import { WBAirplaneFillRepository } from '@/data/WBAirplaneFillRepository/WBAirplaneFillRepository'
import { IWBProfileAirplaneRepository } from '@/data/WBProfileAirplaneRepository/IWBProfileAirplaneRepository'
import { WBProfileAirplaneRepository } from '@/data/WBProfileAirplaneRepository/WBProfileAirplaneRepository'
import { IWBProfileAirplaneTemplateRepository } from '@/data/WBProfileAirplaneTemplateRepository/IWBProfileAirplaneTemplateRepository'
import { WBProfileAirplaneTemplateRepository } from '@/data/WBProfileAirplaneTemplateRepository/WBProfileAirplaneTemplateRepository'

import { InjectionTokens } from './tokens'

import { I18n } from '@/i18n/I18n'
import { AccountWebClient } from '@/infra/Account/AccountWebClient'
import { IAccountWebClient } from '@/infra/Account/IAccountWebClient'
import { DatasetCache } from '@/infra/datasetCache/DatasetCache'
import { IFeatureFlagsProvider } from '@/infra/featureFlags/IFeatureFlagsProvider'
import { LocalFeatureFlagsProvider } from '@/infra/featureFlags/LocalFeatureFlagsProvider'
import { IGeocodeApi } from '@/infra/Geocode/IGeocodeApi'
import { MapboxApi } from '@/infra/Geocode/MapboxApi'
import { ChartConverterWebClient } from '@/infra/http/ChartConverterWebClient/ChartConverterWebClient'
import { IChartConverterWebClient } from '@/infra/http/ChartConverterWebClient/IChartConverterWebClient'
import { IUserChartsWebClient } from '@/infra/http/UserChartsWebClient/IUserChartsWebClient'
import { UserChartsWebClient } from '@/infra/http/UserChartsWebClient/UserChartsWebClient'
import { IUserGeometriesWebClient } from '@/infra/http/UserGeometriesWebClient/IUserGeometriesWebClient'
import { UserGeometriesWebClient } from '@/infra/http/UserGeometriesWebClient/UserGeometriesWebClient'
import { JetStreamRasterClient } from '@/infra/jestStreamClient/JetStreamRasterClient'
import { DexieDB } from '@/infra/localDB/DexieDB'
import { AmplitudeMetricsProvider } from '@/infra/MetricsProvider/AmplitudeMetricsProvider'
import { IGenericMetricsProvider } from '@/infra/MetricsProvider/IGenericMetricsProvider'

export class InversionContainer {
  private static instance: InversionContainer
  private container: Container

  private constructor() {
    this.container = new Container({
      defaultScope: 'Singleton',
      skipBaseClassChecks: true
    })

    this.container.bind<I18n>(InjectionTokens.I18n).to(I18n)
    this.container.bind<IFeatureFlagsProvider>(InjectionTokens.FeatureFlagsProvider).to(LocalFeatureFlagsProvider)
    this.container.bind<ILogger>(InjectionTokens.Logger).to(LocalLogger)

    this.container.bind<IHttpClient>(InjectionTokens.HttpClient).to(AxiosHttpClient)
    this.container.bind<ICachedHttpClient>(InjectionTokens.CachedHttpClient).to(LocalCachedHttpClient)
    this.container.bind<IGeocodeApi>(InjectionTokens.GeocodeApi).to(MapboxApi)
    this.container.bind<IAccountWebClient>(InjectionTokens.AccountWebClient).to(AccountWebClient)
    this.container.bind<JetStreamRasterClient>(InjectionTokens.JetStreamRasterClient).toDynamicValue((context) => {
      const httpClient = context.container.get<IHttpClient>(InjectionTokens.HttpClient)

      // Manifesto e tiles pela CDN. Aqui o ganho não é compressão — tile de terreno é binário e a
      // borda não comprime nenhum dos dois —, é latência: medido em 04/09, um tile de 131 096 bytes
      // desce em ~85 ms pela CDN contra ~2 900 ms pela origem, e o cálculo de altitude de rota busca
      // vários tiles seguidos. Os bytes são idênticos (mesmo SHA-256), então o parse não muda.
      //
      // O `max-age=3600` da borda, que segurou esta migração na PLA-317, não tem o que defasar neste
      // layer: o manifesto do terreno declara um único timestamp e uma única altitude, e está parado
      // desde agosto de 2025. Terreno não é meteorologia (PLA-331).
      const jetstreamUrl = import.meta.env.VITE_JETSTREAM_CDN_BASEURL
      const layer = 'terrain_fb'
      const cacheSize = 100

      return new JetStreamRasterClient(httpClient, jetstreamUrl, layer, cacheSize)
    })
    this.container
      .bind(InjectionTokens.DatasetCache)
      .toDynamicValue((context) => new DatasetCache(context.container.get(InjectionTokens.Logger)))
      .inSingletonScope()
    this.container.bind(InjectionTokens.DexieDB).to(DexieDB)
    this.container.bind<IGenericMetricsProvider>(InjectionTokens.AmplitudeMetricsProvider).to(AmplitudeMetricsProvider)

    this.container.bind<IKeyValuePersister>(InjectionTokens.KeyValuePersister).to(LocalStorageKeyValuePersister)

    // Global States
    this.container.bind<MapLayerState>(InjectionTokens.MapLayerState).to(MapLayerState)
    // Transient upload state: holds File/AbortController, not persisted (kept out of StatePersisterStates).
    this.container.bind<UserChartUploadState>(InjectionTokens.UserChartUploadState).to(UserChartUploadState)
    // Transient geometry upload state: file/progress/stage/cancel handle, not persisted.
    this.container.bind<UserGeometryUploadState>(InjectionTokens.UserGeometryUploadState).to(UserGeometryUploadState)
    // Runtime per-layer visibility of user geometries (Q6): not persisted.
    this.container
      .bind<UserGeometryLayerVisibilityState>(InjectionTokens.UserGeometryLayerVisibilityState)
      .to(UserGeometryLayerVisibilityState)
    // Persisted per-point map visibility of user waypoints (added to StatePersisterStates below).
    this.container
      .bind<UserWaypointVisibilityState>(InjectionTokens.UserWaypointVisibilityState)
      .to(UserWaypointVisibilityState)
    this.container.bind<ProceduresState>(InjectionTokens.ProceduresState).to(ProceduresState)
    this.container.bind<RouteState>(InjectionTokens.RouteState).to(RouteState)
    this.container.bind<SettingsState>(InjectionTokens.SettingsState).to(SettingsState)
    this.container.bind<MapState>(InjectionTokens.MapState).to(MapState)
    this.container.bind<AuthState>(InjectionTokens.AuthState).to(AuthState)
    this.container.bind<FlightState>(InjectionTokens.FlightState).to(FlightState)
    this.container.bind<RouteNavigationState>(InjectionTokens.RouteNavigationState).to(RouteNavigationState)

    this.container.bind<IService>(InjectionTokens.Authenticator).to(Authenticator)
    this.container.bind<SerializableState<any>[]>(InjectionTokens.StatePersisterStates).toConstantValue([
      this.container.get<MapLayerState>(InjectionTokens.MapLayerState),
      this.container.get<RouteState>(InjectionTokens.RouteState),
      this.container.get<SettingsState>(InjectionTokens.SettingsState),
      this.container.get<FlightState>(InjectionTokens.FlightState),
      this.container.get<UserWaypointVisibilityState>(InjectionTokens.UserWaypointVisibilityState)
      // this.container.get<LocalTempDBState>(InjectionTokens.LocalTempDBState)
    ])
    this.container.bind<IService>(InjectionTokens.StatePersister).to(StatePersister)
    this.container.bind<MetricsService>(InjectionTokens.MetricsService).to(MetricsService)
    this.container.bind<ITrackingService>(InjectionTokens.TrackingService).to(TrackingService)
    this.container.bind<RouteStateSideEffects>(InjectionTokens.RouteStateSideEffects).to(RouteStateSideEffects)
    this.container.bind<IService>(InjectionTokens.CruiseAltitudeSuggester).to(CruiseAltitudeSuggester)

    // Repositories
    this.container.bind<IFeatureFlagsRepository>(InjectionTokens.FeatureFlagsRepository).to(FeatureFlagsRepository)
    this.container.bind<IAirspacesRepository>(InjectionTokens.AirspaceRepository).to(JetStreamAirspacesRepository)
    this.container.bind<IMeteorologyRepository>(InjectionTokens.MeteorologyRepository).to(RemoteMeteorologyRepository)
    this.container.bind<IAeroInfoRepository>(InjectionTokens.AeroInfoRepository).to(AeroInfoRepository)
    this.container.bind<IUserRoutesRepository>(InjectionTokens.UserRoutesRepository).to(UserRoutesRepository)
    this.container.bind<IUserWaypointsRepository>(InjectionTokens.UserWaypointsRepository).to(UserWaypointsRepository)
    this.container.bind<IProceduresRepository>(InjectionTokens.ProceduresRepository).to(ProceduresRepository)
    this.container.bind<IAircraftModelRepository>(InjectionTokens.AircraftModelRepository).to(AircraftModelRepository)
    this.container.bind<IUserAircraftsRepository>(InjectionTokens.UserAircraftsRepository).to(UserAircraftsRepository)
    this.container
      .bind<IDefaultWeightsRepository>(InjectionTokens.DefaultWeightsRepository)
      .to(DefaultWeightsRepository)
    this.container.bind<IFuelRepository>(InjectionTokens.FuelRepository).to(FuelRepository)
    this.container
      .bind<IWBAirplaneFillRepository>(InjectionTokens.WBAirplaneFillRepository)
      .to(WBAirplaneFillRepository)
    this.container
      .bind<IWBProfileAirplaneRepository>(InjectionTokens.WBProfileAirplaneRepository)
      .to(WBProfileAirplaneRepository)
    this.container
      .bind<IWBProfileAirplaneTemplateRepository>(InjectionTokens.WBProfileAirplaneTemplateRepository)
      .to(WBProfileAirplaneTemplateRepository)
    this.container.bind<INotamRepository>(InjectionTokens.NotamRepository).to(NotamRepository)
    this.container.bind<IObstacleRepository>(InjectionTokens.ObstacleRepository).to(ObstacleRepository)
    this.container.bind<IUserChartRepository>(InjectionTokens.UserChartRepository).to(UserChartRepository)
    this.container.bind<IUserGeometryRepository>(InjectionTokens.UserGeometryRepository).to(UserGeometryRepository)

    // User Charts web clients
    this.container.bind<IChartConverterWebClient>(InjectionTokens.ChartConverterWebClient).to(ChartConverterWebClient)
    this.container.bind<IUserChartsWebClient>(InjectionTokens.UserChartsWebClient).to(UserChartsWebClient)

    // User Charts services
    this.container.bind<UserChartSyncService>(InjectionTokens.UserChartSyncService).to(UserChartSyncService)
    this.container
      .bind<UserChartTilesPrefetchService>(InjectionTokens.UserChartTilesPrefetchService)
      .to(UserChartTilesPrefetchService)

    // User Charts use cases
    const userChartRepository = () => this.container.get<IUserChartRepository>(InjectionTokens.UserChartRepository)
    this.container
      .bind<DeleteUserChartsLocalDataUseCase>(InjectionTokens.DeleteUserChartsLocalDataUseCase)
      .to(DeleteUserChartsLocalDataUseCase)
    this.container
      .bind<CheckUserChartNameExistsUseCase>(InjectionTokens.CheckUserChartNameExistsUseCase)
      .toDynamicValue(() => new CheckUserChartNameExistsUseCase())
    this.container
      .bind<DeleteUserChartUseCase>(InjectionTokens.DeleteUserChartUseCase)
      .toDynamicValue(() => new DeleteUserChartUseCase())
    this.container
      .bind<DownloadUserChartOriginalUseCase>(InjectionTokens.DownloadUserChartOriginalUseCase)
      .toDynamicValue(() => new DownloadUserChartOriginalUseCase())
    this.container
      .bind<LoadUserChartsUseCase>(InjectionTokens.LoadUserChartsUseCase)
      .toDynamicValue(() => new LoadUserChartsUseCase())
    this.container
      .bind<LoadUserChartUseCase>(InjectionTokens.LoadUserChartUseCase)
      .toDynamicValue(() => new LoadUserChartUseCase())
    this.container
      .bind<PrepareUserChartUseCase>(InjectionTokens.PrepareUserChartUseCase)
      .toDynamicValue(() => new PrepareUserChartUseCase(userChartRepository()))
    this.container
      .bind<RefreshUserChartsUseCase>(InjectionTokens.RefreshUserChartsUseCase)
      .toDynamicValue(() => new RefreshUserChartsUseCase())
    this.container
      .bind<SaveUserChartUseCase>(InjectionTokens.SaveUserChartUseCase)
      .toDynamicValue(() => new SaveUserChartUseCase())
    this.container
      .bind<UpdateUserChartUseCase>(InjectionTokens.UpdateUserChartUseCase)
      .toDynamicValue(() => new UpdateUserChartUseCase())

    // User Geometries web client
    this.container.bind<IUserGeometriesWebClient>(InjectionTokens.UserGeometriesWebClient).to(UserGeometriesWebClient)

    // User Geometries services
    this.container.bind<UserGeometrySyncService>(InjectionTokens.UserGeometrySyncService).to(UserGeometrySyncService)

    // User Geometries use cases
    const userGeometryRepository = () =>
      this.container.get<IUserGeometryRepository>(InjectionTokens.UserGeometryRepository)
    this.container
      .bind<CheckUserGeometryFileExistsUseCase>(InjectionTokens.CheckUserGeometryFileExistsUseCase)
      .toDynamicValue(() => new CheckUserGeometryFileExistsUseCase(userGeometryRepository()))
    this.container
      .bind<CheckUserGeometryNameExistsUseCase>(InjectionTokens.CheckUserGeometryNameExistsUseCase)
      .toDynamicValue(() => new CheckUserGeometryNameExistsUseCase(userGeometryRepository()))
    this.container
      .bind<DeleteUserGeometriesLocalDataUseCase>(InjectionTokens.DeleteUserGeometriesLocalDataUseCase)
      .toDynamicValue(() => new DeleteUserGeometriesLocalDataUseCase(userGeometryRepository()))
    this.container
      .bind<DeleteUserGeometryUseCase>(InjectionTokens.DeleteUserGeometryUseCase)
      .toDynamicValue(() => new DeleteUserGeometryUseCase(userGeometryRepository()))
    this.container
      .bind<LoadUserGeometriesUseCase>(InjectionTokens.LoadUserGeometriesUseCase)
      .toDynamicValue(() => new LoadUserGeometriesUseCase(userGeometryRepository()))
    this.container
      .bind<LoadUserGeometryUseCase>(InjectionTokens.LoadUserGeometryUseCase)
      .toDynamicValue(() => new LoadUserGeometryUseCase(userGeometryRepository()))
    this.container
      .bind<PrepareUserGeometryUseCase>(InjectionTokens.PrepareUserGeometryUseCase)
      .toDynamicValue(() => new PrepareUserGeometryUseCase(userGeometryRepository()))
    this.container
      .bind<SaveUserGeometryUseCase>(InjectionTokens.SaveUserGeometryUseCase)
      .toDynamicValue(() => new SaveUserGeometryUseCase(userGeometryRepository()))
    this.container
      .bind<UpdateUserGeometryUseCase>(InjectionTokens.UpdateUserGeometryUseCase)
      .toDynamicValue(() => new UpdateUserGeometryUseCase(userGeometryRepository()))
    this.container
      .bind<UpdateGeometriesLayersUseCase>(InjectionTokens.UpdateGeometriesLayersUseCase)
      .toDynamicValue(() => new UpdateGeometriesLayersUseCase(userGeometryRepository()))

    this.container.bind<INavaidRepository>(InjectionTokens.NavaidRepository).to(NavaidRepository)
    // useCases
    this.container.bind<UndoRouteUseCase>(InjectionTokens.UndoRouteUseCase).to(UndoRouteUseCase)
  }

  public static getInstance() {
    if (!InversionContainer.instance) {
      InversionContainer.instance = new InversionContainer()
    }

    return InversionContainer.instance
  }

  public getContainer() {
    return this.container
  }

  public resetContainer() {
    this.container.unbindAll()
  }
}
